import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const mobileRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        mobileRef.current &&
        !mobileRef.current.contains(target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(target)
      ) {
        setMobileOpen(false);
        setMobileCatOpen(false);
      }
    };
    if (mobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleCategoriesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById('categorias')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById('categorias')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleMobileCategoriesClick = () => {
    setMobileOpen(false);
    setMobileCatOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById('categorias')?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    } else {
      setTimeout(() => {
        document.getElementById('categorias')?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-background-50/80 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0" onClick={() => setMobileOpen(false)}>
            <h1 className="font-heading text-lg md:text-xl font-bold text-foreground-900 tracking-tight whitespace-nowrap">
              CSSBuy<span className="text-primary-500">Spreadsheet</span>
            </h1>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-semibold text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap rounded-lg hover:bg-background-100"
            >
              Início
            </Link>
            <a
              href="/#categorias"
              onClick={handleCategoriesClick}
              className="px-4 py-2 text-sm font-semibold text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap rounded-lg hover:bg-background-100 cursor-pointer"
            >
              Categorias
            </a>
            <Link
              to="/artigo-cssbuyspreadsheet-guia-completo-2026"
              className="px-4 py-2 text-sm font-semibold text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap rounded-lg hover:bg-background-100"
            >
              Guia de Compras
            </Link>
            <div className="w-px h-5 bg-background-300 mx-2"></div>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap"
            >
              CSSBuy Sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-shopping-bag-line"></i>
            </a>
          </div>

          <button
            ref={hamburgerRef}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 bg-background-100 hover:bg-background-200 transition-colors"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          ref={mobileRef}
          className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto shadow-lg"
        >
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 text-sm font-semibold text-foreground-700 rounded-lg hover:bg-background-100 transition-colors flex items-center gap-2"
          >
            <i className="ri-home-line text-primary-500"></i>
            Início
          </Link>

          <button
            onClick={handleMobileCategoriesClick}
            className="text-left px-4 py-3 text-sm font-semibold text-foreground-700 rounded-lg hover:bg-background-100 transition-colors flex items-center gap-2"
          >
            <i className="ri-grid-line text-primary-500"></i>
            Categorias
          </button>

          <Link
            to="/artigo-cssbuyspreadsheet-guia-completo-2026"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 text-sm font-semibold text-foreground-700 rounded-lg hover:bg-background-100 transition-colors flex items-center gap-2"
          >
            <i className="ri-book-open-line text-primary-500"></i>
            Guia de Compras
          </Link>

          <div className="border-t border-background-200 my-2"></div>

          <button
            onClick={() => setMobileCatOpen((prev) => !prev)}
            className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-700 rounded-lg hover:bg-background-100 transition-colors"
          >
            <span className="flex items-center gap-2">
              <i className="ri-shopping-bag-3-line text-primary-500"></i>
              Todas as Categorias
            </span>
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {mobileCatOpen && (
            <div className="ml-4 border-l-2 border-primary-200 pl-3 py-1 space-y-0.5">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => { setMobileOpen(false); setMobileCatOpen(false); }}
                  className="block px-4 py-2.5 text-sm text-foreground-600 hover:text-primary-500 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  {cat.namePt}
                </a>
              ))}
            </div>
          )}

          <div className="border-t border-background-200 my-2"></div>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap"
          >
            CSSBuy Sheet <i className="ri-arrow-right-up-line"></i>
          </a>
        </div>
      )}
    </nav>
  );
}